// Color utility composable for consistent color handling across components

/**
 * Convert color names to Tailwind background classes
 */
export const colorNameToBgClass = (colorName) => {
  if (!colorName) return 'bg-base-100'
  
  // Handle space-separated classes (e.g., "sky-200 dark:sky-800")
  if (colorName.includes(' ')) {
    return colorName.split(' ').map(cls => {
      // If it already has bg- prefix or is a dark: modifier, return as-is
      if (cls.startsWith('bg-') || cls.startsWith('dark:bg-')) {
        return cls
      }
      // If it's a dark: modifier without bg-, add bg- after dark:
      if (cls.startsWith('dark:')) {
        return cls.replace('dark:', 'dark:bg-')
      }
      // Otherwise add bg- prefix
      return `bg-${cls}`
    }).join(' ')
  }
  
  // Single class - add bg- prefix if not already present
  if (colorName.startsWith('bg-')) {
    return colorName
  }
  return `bg-${colorName}`
}

/**
 * Convert color names to CSS custom properties/values
 * Supports DaisyUI semantic colors, custom form colors, and common zinc colors
 */
export const colorNameToCssVar = (colorName) => {
  // If it's already a CSS variable or color value, return as-is
  if (colorName.startsWith('var(') || colorName.startsWith('#') || colorName.startsWith('oklch(')) {
    return colorName
  }
  
  // Handle space-separated classes with dark mode (e.g., "sky-300 dark:neutral")
  if (colorName.includes(' ')) {
    const classes = colorName.split(' ')
    const lightClass = classes.find(cls => !cls.startsWith('dark:'))
    const darkClass = classes.find(cls => cls.startsWith('dark:'))
    
    // Check if we're in dark mode by looking at the document root
    const isDark = document.documentElement.getAttribute('data-theme') === 'night'
    
    if (isDark && darkClass) {
      // Use dark class, remove 'dark:' prefix
      const darkColor = darkClass.replace('dark:', '')
      return getColorValue(darkColor)
    } else if (lightClass) {
      // Use light class
      return getColorValue(lightClass)
    }
  }
  
  // Single color
  return getColorValue(colorName)
}

// Helper function to get actual color values
function getColorValue(colorName) {
  // Map semantic color names to CSS variables
  const mapping = {
    // DaisyUI semantic colors
    'primary': 'oklch(var(--p))',
    'secondary': 'oklch(var(--s))',
    'accent': 'oklch(var(--a))',
    'neutral': 'oklch(var(--n))',
    'base-100': 'oklch(var(--b1))',
    'base-200': 'oklch(var(--b2))',
    'base-300': 'oklch(var(--b3))',
    'base-400': 'oklch(var(--b4))',
    'base-content': 'oklch(var(--bc))',
    'success': 'oklch(var(--su))',
    'warning': 'oklch(var(--wa))',
    'error': 'oklch(var(--er))',
    'info': 'oklch(var(--in))',
    
    // Custom form colors
    'form-border': 'var(--form-border)',
    'form-border-focus': 'var(--form-border-focus)',
    'form-border-hover': 'var(--form-border-hover)',
    
    // Common Tailwind colors
    'zinc-300': '#d4d4d8',
    'zinc-400': '#a1a1aa',
    'zinc-500': '#71717a',
    'sky-300': '#7dd3fc',
    'sky-400': '#38bdf8',
    'sky-500': '#0ea5e9',
    'sky-600': '#0284c7',
    'sky-700': '#0369a1',
    'sky-800': '#075985',
  }
  
  return mapping[colorName] || colorName
}